import {
  Text,
  StandardLayout,
  Button,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  RevenueWaterfallQueryParams,
  RevenueWaterfallTableFilters
} from './content/RevenueWaterfallTable'
import { RevenueWaterfallTable } from './content/RevenueWaterfallTable/RevenueWaterfallTable'
import { RevenueWaterfallTableSkeleton } from './content/RevenueWaterfallTable/RevenueWaterfallTableSkeleton'
import { RevenueWaterfallHeader } from './content/RevenueWaterfallHeader'
import { useEffect, useState } from 'react'
import { MagicTableEmptyState } from '@sequencehq/core-components'
import { useSelectedCurrency } from '../utils/useSelectedCurrency'
import { useRevenueWaterfallData } from './content/RevenueWaterfallTable/useRevenueWaterfallData'
import { RevenueWaterfallInspector } from './content/RevenueWaterfallInspector'
import {
  TableToChatProvider,
  useTableToChat
} from './context/TableToChatContext'
import { useRevenueWaterfallTableParams } from './hooks/useRevenueWaterfallFilters'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { match } from 'ts-pattern'
import { useRevenueWaterfallSorting } from './hooks/useRevenueWaterfallSorting'
import { Currency } from '@sequencehq/api'

const RevenueWaterfallContent = () => {
  const { filters, setFilters } = useRevenueWaterfallTableParams()
  const { handleSort, sortParams } = useRevenueWaterfallSorting()
  const { currency: _currency } = useSelectedCurrency()
  const currency = _currency ?? 'USD'

  const fullFilters: RevenueWaterfallQueryParams = {
    currency,

    bookedFrom: filters.bookedFrom,
    bookedTo: filters.bookedTo,
    recognizedUpTo: filters.recognizedUpTo,
    customerId: filters.customerId,
    productId: filters.productId,
    groupBy: filters.groupBy,
    subGroupBy: filters.subGroupBy,

    sortBy: sortParams.sortBy,
    sortDirection: sortParams.sortDirection
  }

  const waterfall = useRevenueWaterfallData(fullFilters)

  const { status, data, meta } = waterfall
  const showTableFetchingState = status === 'ready' ? meta.isRefetching : false

  const { addSelectionToChat, WaterfallInspector } = useChattyRevRecInspector({
    currency,
    waterfallData: waterfall,
    filters
  })

  let content = null
  content = match(status)
    .with('loading', () => {
      return (
        <Box overflow="auto" flex={1} minWidth={0}>
          <Flex
            p={4}
            flexDirection="column"
            gap={6}
            data-testid="revrec.waterfall.state.loading"
          >
            <RevenueWaterfallTableSkeleton
              asOfDate={filters.recognizedUpTo}
              monthCount={12}
            />
          </Flex>
        </Box>
      )
    })
    .with('empty', () => {
      return (
        <MagicTableEmptyState
          data-testid="revrec.waterfall.state.empty"
          entityNamePlural="entries"
          imageSrc="/img/empty/invoices.png"
          learnMoreText="There are no entries for the selected filters right now"
          learnMoreHref="https://docs.sequencehq.com/revenue-recognition"
        />
      )
    })
    .with('error', () => {
      return (
        <Flex
          flex={1}
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={4}
          data-testid="revrec.waterfall.state.error"
        >
          <Text fontSize="sm" color="gray.500">
            Something went wrong while loading the revenue waterfall.
          </Text>
          <Button
            size="32px"
            variant="secondary"
            onClick={() => {
              void meta.refetch()
            }}
          >
            Try again
          </Button>
        </Flex>
      )
    })
    .with('ready', () => {
      // Even though the types are sound, ts-pattern and typsecript don't seem to understand
      // when the status is 'ready' the data _shouldn't_ be null
      if (!data) {
        return null
      }

      return (
        <Box overflow="auto" flex={1} minWidth={0}>
          <Flex p={4} flexDirection="column" gap={6}>
            <RevenueWaterfallTable
              data={data}
              currency={currency ?? 'GBP'}
              asOfDate={filters.recognizedUpTo}
              primaryGroup={filters.groupBy}
              sortState={sortParams}
              isFetching={showTableFetchingState}
              fetchNextPage={meta.fetchNextPage}
              hasNextPage={meta.hasNextPage}
              isFetchingNextPage={meta.isFetchingNextPage}
              onSort={handleSort}
              onAddToChat={addSelectionToChat}
            />
          </Flex>
        </Box>
      )
    })
    .otherwise(() => {
      return null
    })

  return (
    <StandardLayout
      header={
        <>
          <RevenueWaterfallHeader filters={fullFilters} />
          <RevenueWaterfallTableFilters
            filters={filters}
            onFilterChange={setFilters}
          />
        </>
      }
      content={content}
      overlayInspector
      inspector={WaterfallInspector}
    />
  )
}

export const RevenueWaterfall = () => {
  return (
    <TableToChatProvider>
      <RevenueWaterfallContent />
    </TableToChatProvider>
  )
}

// Putting this here because chatty revrec as a whole is experimental and I wanted to
// remove as much of it as possible from the proper waterfall code
export const useChattyRevRecInspector = ({
  currency,
  waterfallData,
  filters
}: {
  currency?: Currency
  waterfallData: ReturnType<typeof useRevenueWaterfallData>
  filters: RevenueWaterfallQueryParams
}) => {
  const flags = useFeatureFlags()

  const { addSelectionToChat, lastAddedSelection, clearLastSelection } =
    useTableToChat()

  const [inspectorControl, setInspectorControl] = useState<{
    expanded?: boolean
    showItemId?: string
  }>({})

  // Open AI inspector when a selection is added; release control shortly after
  // so the inspector returns to uncontrolled mode.
  useEffect(() => {
    if (!lastAddedSelection) return
    setInspectorControl({ expanded: true, showItemId: 'revenueWaterfall.ai' })
    const t = setTimeout(() => setInspectorControl({}), 300)
    const c = setTimeout(() => clearLastSelection(), 2000)
    return () => clearTimeout(t)
  }, [lastAddedSelection, clearLastSelection])

  const WaterfallInspector = flags.useChattyRevRec ? (
    <RevenueWaterfallInspector
      revenueWaterfallData={waterfallData}
      revenueWaterfallFilters={filters}
      currency={currency || undefined}
      expanded={inspectorControl.expanded}
      showItemId={inspectorControl.showItemId}
    />
  ) : undefined

  return {
    addSelectionToChat,
    WaterfallInspector
  }
}
